import type { TFunction } from 'i18next';
import { z } from 'zod';

function buildMinPointRule(t: TFunction) {
  return z.coerce
    .number({ invalid_type_error: t('admin.shops.form.min.point.invalid') })
    .int(t('admin.shops.form.min.point.integer'))
    .min(0, t('admin.shops.form.min.point.min'));
}

/** Variante obligatoire de `minPoint`, à passer en `fieldSchema` quand `hasMinPoint` est vrai. */
export function buildRequiredMinPointSchema(t: TFunction) {
  return buildMinPointRule(t);
}

/** Construit le schéma de base (sans règles inter-champs) — utilisé aussi par `FormLabel` pour le required-marker. */
export function buildCreateCustomShopFormBaseSchema(t: TFunction) {
  // Obligatoire pour les boutiques custom (contrainte backend conditionnelle sur `type`,
  // non exprimée dans le schéma généré qui reste `.nullish()` toutes boutiques confondues).
  const requiredEmail = z
    .string()
    .trim()
    .min(1, t('admin.shops.form.email.required'))
    .email(t('admin.shops.form.email.invalid'))
    .max(255, t('admin.shops.form.email.max'));

  return z.object({
    displayName: z
      .string()
      .trim()
      .min(1, t('admin.shops.form.display.name.required'))
      .max(100, t('admin.shops.form.display.name.max')),
    description: z
      .string()
      .trim()
      .min(1, t('admin.shops.form.description.required'))
      .max(255, t('admin.shops.form.description.max')),
    logo: z.custom<File>(
      (value) => value instanceof File && value.type.startsWith('image/'),
      t('admin.shops.form.logo.required')
    ),
    banner: z.custom<File | undefined>(
      (value) => value === undefined || (value instanceof File && value.type.startsWith('image/')),
      t('admin.shops.form.file.image.invalid')
    ),
    operationalContact: requiredEmail,
    technicalContact: requiredEmail,
    isActive: z.boolean(),
    shopOpeningDate: z.string().optional(),
    shopClosingDate: z.string().optional(),
    hasMinPoint: z.boolean(),
    minPoint: buildMinPointRule(t).optional()
  });
}

/**
 * Construit le schéma du formulaire avec des messages traduits et valide les
 * règles qui dépendent de plusieurs champs (dates et seuil de points).
 */
export function buildCreateCustomShopFormSchema(t: TFunction) {
  return buildCreateCustomShopFormBaseSchema(t).superRefine((values, context) => {
    // Les dates au format ISO peuvent être comparées directement dans l'ordre chronologique.
    if (
      values.shopOpeningDate &&
      values.shopClosingDate &&
      values.shopClosingDate <= values.shopOpeningDate
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['shopClosingDate'],
        message: t('admin.shops.form.closing.date.after.opening')
      });
    }

    // Le seuil devient obligatoire uniquement lorsque l'utilisateur active cette option.
    if (values.hasMinPoint && values.minPoint === undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minPoint'],
        message: t('admin.shops.form.min.point.required')
      });
    }
  });
}

export type CreateCustomShopFormValues = z.infer<
  ReturnType<typeof buildCreateCustomShopFormSchema>
>;

export const PERSONALIZATION_FIELDS: Array<keyof CreateCustomShopFormValues> = [
  'displayName',
  'description',
  'logo',
  'banner'
];
